import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/local.js';
import type { LocalPlan, LocalPlanDay, LocalWorkoutSession } from '../../db/local.js';
import { activePlan, listDays } from '../../db/plans.js';
import {
  endForgottenSession,
  listSessions,
  openSession,
  rateSession,
  startSession,
} from '../../db/workouts.js';
import { contextForSession } from '../../db/journal.js';
import {
  isForgottenSession,
  isoDateInZone,
  isWeakSession,
  rotatePlanDays,
} from '@kraftwerk/shared';
import type { SessionContext } from '@kraftwerk/shared';
import { ScalePicker } from '../ScalePicker.js';
import { readProfile } from '../../db/profile.js';
import { useIsChild } from '../../hooks/useModules.js';
import { ExerciseLibrary } from './ExerciseLibrary.js';
import { TrainingDays } from './TrainingDays.js';
import { PlanEditor } from './PlanEditor.js';
import { ProgressPanel } from './ProgressPanel.js';
import { SessionScreen } from './SessionScreen.js';
import { formatDay } from '../../format.js';
import { useTimeZone } from '../../hooks/useToday.js';
import { SwipePages, neighbourIn } from '../../gestures/SwipeProvider.js';

export type TrainingView = 'overview' | 'exercises' | 'plans' | 'progress';
type View = TrainingView;

/**
 * Der Reiter „Training".
 *
 * Läuft gerade eine Einheit, ist sie das Erste, was man sieht — im Gym ist
 * jeder zusätzliche Tipp einer zu viel. Alles andere liegt darunter:
 * Übungen, Pläne, Fortschritt.
 */
const TRAINING_VIEWS = ['overview', 'exercises', 'plans', 'progress'] as const;

export function TrainingScreen({
  initialView = 'overview',
}: {
  initialView?: View | undefined;
} = {}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const [view, setView] = useState<View>(initialView);
  const [inSession, setInSession] = useState(false);

  const running = useLiveQuery(() => openSession(db), [], null) as LocalWorkoutSession | null;

  if (running && inSession) {
    return (
      <SessionScreen
        session={running}
        onExit={() => {
          setInSession(false);
          setView('overview');
        }}
      />
    );
  }

  const tabs: { key: View; label: string }[] = [
    { key: 'overview', label: t('training.tabs.overview') },
    { key: 'exercises', label: t('training.tabs.exercises') },
    { key: 'plans', label: t('training.tabs.plans') },
    { key: 'progress', label: t('training.tabs.progress') },
  ];

  return (
    <div className="flex flex-col gap-3">
      {running && isForgottenSession(running.startedAt) ? (
        // Seit Tagen „läuft“ eine Einheit: fast immer vergessen. Beendet wird
        // beim letzten Satz, nicht jetzt — sonst dauerte sie Tage.
        <section
          aria-labelledby="forgotten-heading"
          className="kw-tile flex flex-col gap-2 border-warn"
        >
          <h2 id="forgotten-heading" className="font-medium">
            {t('training.overview.forgotten', {
              title: running.title,
              date: formatDay(
                isoDateInZone(new Date(running.startedAt), timeZone ?? 'Europe/Berlin'),
                'long',
                i18n.language,
              ),
            })}
          </h2>
          <p className="text-sm text-ink-muted">{t('training.overview.forgottenWhy')}</p>
          <button
            type="button"
            onClick={() => void endForgottenSession(running.id, db)}
            className="kw-btn"
          >
            {t('training.overview.forgottenEnd')}
          </button>
          <button
            type="button"
            onClick={() => setInSession(true)}
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {t('training.overview.forgottenResume')}
          </button>
        </section>
      ) : running ? (
        <button type="button" onClick={() => setInSession(true)} className="kw-hero text-left">
          <span className="kw-kicker text-accent">{t('training.overview.resume')}</span>
          <span className="kw-figure mt-1 block truncate text-2xl">{running.title}</span>
        </button>
      ) : null}

      <nav aria-label={t('training.tabs.label')}>
        <ul className="flex gap-1 rounded-full border border-line bg-surface p-1">
          {tabs.map((tab) => (
            <li key={tab.key} className="flex-1">
              <button
                type="button"
                aria-current={view === tab.key ? 'page' : undefined}
                onClick={() => setView(tab.key)}
                className={`kw-btn kw-btn-small w-full px-2 ${
                  view === tab.key ? '' : 'border-0 bg-transparent text-ink-muted'
                }`}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Wischen auf dem Inhalt wechselt die Unterreiter (24.10.2026). Während
          der Sätze gibt es diese Ansicht nicht — dort keine Gesten. */}
      <SwipePages
        current={view}
        neighbour={neighbourIn(TRAINING_VIEWS)}
        onChange={setView}
        render={(key) =>
          key === 'overview' ? (
            <Overview
              hasRunning={running !== null}
              onStarted={() => setInSession(true)}
              onToPlans={() => setView('plans')}
            />
          ) : key === 'exercises' ? (
            <ExerciseLibrary />
          ) : key === 'plans' ? (
            <PlanEditor />
          ) : (
            <ProgressPanel />
          )
        }
      />
    </div>
  );
}

function Overview({
  hasRunning,
  onStarted,
  onToPlans,
}: {
  hasRunning: boolean;
  onStarted: () => void;
  onToPlans: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  // Ein Teen (28.10.2026) trainiert ohne Körpergewicht an der Einheit.
  const child = useIsChild();

  const plan = useLiveQuery(() => activePlan(db), [], null) as LocalPlan | null;
  const days = useLiveQuery(
    async () => (plan ? await listDays(plan.id, db) : []),
    [plan?.id],
    [] as LocalPlanDay[],
  );
  const recent = useLiveQuery(() => listSessions(db, 5), [], [] as LocalWorkoutSession[]);
  // Welcher Plan-Tag dran ist — dieselbe Rotation wie im Kalender.
  const lastPlanDayId = useLiveQuery(
    async () =>
      (await db.workout_sessions.toArray())
        .filter((session) => !session.deletedAt && session.planDayId)
        .sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0]?.planDayId ?? null,
    [],
    null,
  );
  const nextDayId =
    rotatePlanDays(
      [{ status: 'planned' as const }],
      days.map((day) => day.id),
      lastPlanDayId,
    )[0]?.planDayId ?? null;

  const begin = async (title: string, planDayId: string | null) => {
    const profile = await readProfile(db);
    await startSession({ title, planDayId, bodyWeightKg: child ? null : profile.weightKg }, db);
    onStarted();
  };

  return (
    <section aria-labelledby="training-overview-heading" className="flex flex-col gap-4">
      <h2 id="training-overview-heading" className="sr-only">
        {t('training.tabs.overview')}
      </h2>

      {plan ? (
        <div className="kw-hero">
          <p className="kw-kicker">{t('training.overview.activePlan')}</p>
          <p className="kw-figure mt-1 text-2xl">{plan.name}</p>
          {plan.note ? <p className="mt-1 text-xs text-ink-muted">{plan.note}</p> : null}

          <ul className="mt-3 flex flex-col gap-2">
            {days.map((day) => (
              <li
                key={day.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-line bg-surface-raised px-3 py-2"
              >
                <span className="min-w-0 truncate text-sm font-semibold">
                  {day.name}
                  {day.id === nextDayId ? (
                    <span className="block text-xs font-normal text-ink-muted">
                      {t('training.overview.nextUp')}
                    </span>
                  ) : null}
                </span>
                {/* Bernstein nur für den Tag, der dran ist. */}
                <button
                  type="button"
                  disabled={hasRunning}
                  onClick={() => void begin(day.name, day.id)}
                  className={`kw-btn kw-btn-small shrink-0 px-4 ${
                    day.id === nextDayId ? '' : 'kw-btn-quiet'
                  }`}
                >
                  {t('training.overview.start')}
                </button>
              </li>
            ))}
          </ul>
          {days.length === 0 ? (
            <p className="mt-2 text-sm text-ink-muted">{t('training.overview.planWithoutDays')}</p>
          ) : null}
        </div>
      ) : (
        <div className="kw-tile">
          <p className="text-sm">{t('training.overview.noActivePlan')}</p>
          <button
            type="button"
            onClick={onToPlans}
            className="kw-btn kw-btn-quiet kw-btn-small mt-3 w-full"
          >
            {t('training.overview.toPlans')}
          </button>
        </div>
      )}

      <TrainingDays />

      {/* Früher war die ganze Kachel der Knopf — sie sah aus wie ein Hinweis,
          niemand tippte darauf. */}
      <section aria-labelledby="free-session-heading" className="kw-tile flex flex-col gap-2">
        <h3 id="free-session-heading" className="kw-kicker">
          {t('training.overview.freeSessionHeading')}
        </h3>
        <p className="text-xs text-ink-muted">{t('training.overview.freeSessionWhy')}</p>
        <button
          type="button"
          disabled={hasRunning}
          onClick={() => void begin(t('training.overview.freeSessionTitle'), null)}
          className="kw-btn kw-btn-quiet kw-btn-small disabled:opacity-50"
        >
          {t('training.overview.freeSession')}
        </button>
      </section>

      <div className="kw-tile">
        <h3 className="kw-kicker">{t('training.overview.recent')}</h3>
        {recent.length === 0 ? (
          <p className="mt-2 text-sm text-ink-muted">{t('training.overview.noSessions')}</p>
        ) : (
          <ul className="mt-2 flex flex-col gap-1">
            {recent.map((session) => (
              <SessionRow key={session.id} session={session} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/**
 * Eine vergangene Einheit — und bei einer schwachen, was davor war.
 *
 * Die App legt daneben, was in Frage kommt, und zieht keinen Schluss: Ob es der
 * Schlaf war oder das ausgelassene Mittagessen, weiß nur, wer dabei war.
 */
function SessionRow({ session }: { session: LocalWorkoutSession }): React.JSX.Element {
  const { t } = useTranslation();
  const [rating, setRating] = useState(false);
  const weak = isWeakSession(session.rating);

  const context = useLiveQuery(
    async () => {
      if (!weak) return null;
      const profile = await readProfile(db);
      return contextForSession(session, profile.timezone, db);
    },
    [session.id, session.startedAt, weak],
    null,
  );

  return (
    <li className="flex flex-col gap-1 border-b border-line py-2 last:border-b-0">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="min-w-0 truncate">{session.title}</span>
        <span className="shrink-0 text-ink-muted">
          {new Date(session.startedAt).toLocaleDateString()}
          {session.endedAt ? '' : ` · ${t('training.overview.unfinished')}`}
        </span>
      </div>

      {session.endedAt ? (
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className={weak ? 'text-warn' : 'text-ink-muted'}>
            {typeof session.rating === 'number'
              ? t('journal.rating.shown', { rating: session.rating })
              : t('journal.rating.none')}
          </span>
          <button
            type="button"
            aria-expanded={rating}
            onClick={() => setRating((value) => !value)}
            className="kw-btn kw-btn-quiet kw-btn-small px-3"
          >
            {typeof session.rating === 'number'
              ? t('journal.rating.change')
              : t('journal.rating.rate')}
          </button>
        </div>
      ) : null}

      {rating ? (
        <ScalePicker
          label={t('journal.rating.label')}
          low={t('journal.rating.low')}
          high={t('journal.rating.high')}
          value={session.rating ?? null}
          onChange={(value) => {
            void rateSession(session.id, value, db);
            setRating(false);
          }}
        />
      ) : null}

      {weak && context ? <ContextLine context={context} /> : null}
    </li>
  );
}

/** Was vor der Einheit war — fehlende Werte stehen als solche da, nicht als 0. */
function ContextLine({ context }: { context: SessionContext }): React.JSX.Element {
  const { t } = useTranslation();
  const parts = [
    context.kcal === null
      ? t('journal.context.noMeals')
      : t('journal.context.meals', { kcal: context.kcal, protein: context.protein }),
    context.minutesSinceMeal === null
      ? null
      : context.minutesSinceMeal < 60
        ? t('journal.context.lastMealMinutes', { minutes: context.minutesSinceMeal })
        : t('journal.context.lastMeal', {
            hours: Math.floor(context.minutesSinceMeal / 60),
            minutes: context.minutesSinceMeal % 60,
          }),
    context.sleepHours === null
      ? t('journal.context.noSleep')
      : t('journal.context.sleep', { hours: context.sleepHours }),
    context.energy === null ? null : t('journal.context.energy', { energy: context.energy }),
  ].filter((part) => part !== null);

  return (
    <p className="rounded-lg bg-surface-raised px-2 py-1 text-xs text-ink-muted">
      <span className="font-semibold text-ink">{t('journal.context.before')}</span>{' '}
      {parts.join(' · ')}
    </p>
  );
}
